<script setup>
// 골드나라 웹 공통 상단 — 시세 바(40) + 메뉴(60) + 시세표 띠(34) = 134px
//
// 퍼블리싱은 시세 바 50 + 헤더 90 = 140 이었다. 2026-09-26 에 헤더 90 을
// 메뉴 60 + 시세표 띠 30 으로 나눴고, 같은 날 시세 바를 40 으로 줄이고
// 띠를 34 로 키웠다. 합이 바뀌면 useFitHeight 의 HEADER_H 도 같이 옮겨야 한다 —
// 섹션이 한 화면에 들어가는지 재는 기준이 그 값이다.
//
// Figma: xsS20cl9Iwm2XO7WPQkg54
//   시세 바 377:21382 / 헤더 377:21421 / 로그인 툴팁 380:22861
// 홈 · IR · 자사몰 · 미디어 허브 · 미디어 허브 전체보기 5개 화면에 동일하게 들어간다.
//
// ★ 이 파일은 세 저장소에 "복사"해서 쓴다.
//     homepage / goldnaramall-client / mediahub-client
//   고칠 때는 세 곳을 모두 같이 고칠 것. 한 곳만 고치면 화면마다 상단이 달라진다.
//
// 콘텐츠 폭은 1240 중앙정렬이다 — 네 사이트(자사몰 · 홈페이지 · 미디어허브) 헤더가 모두 같다.
// 좌우 20px 여백을 폭에 포함해 max-w-[1280px] + px-[20px] 로 둔다. 1920 에서 콘텐츠가 340 에서
// 시작하고, 좁은 화면에서도 가장자리에 붙지 않는다.
//
// 2026-11-05 에 1490(max-w-[1280px], Figma 215~1705)에서 1240 으로 바꿨다(지시: 헤더 · 푸터를
// 자사몰과 같은 1240 으로). 그전에는 사이트마다 본문 폭을 따라 자사몰만 1240 이라, 사이트를
// 오갈 때 로고와 오른쪽 끝이 1440 창에서 65px 씩 움직였다. 본문 폭은 그대로다(미디어허브 1490).
// (홈은 1920 캔버스를 축소해 그리므로 Tailwind 반응형 분기는 쓸 수 없다 —
//  분기는 실제 뷰포트를 보기 때문에 캔버스 안에서는 의도대로 동작하지 않는다.)
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { SITE_MENU } from '@/data/siteMenu.js'
import RatesTicker from '@/components/common/RatesTicker.vue'
import { useHeaderRates } from '@/composables/useHeaderRates.js'
import imgTickerGlow from '@/assets/figma/d13030e647b9e060f2375cad0d1c77f92d50085f.png'
import imgArrowDown from '@/assets/figma/b3890e2308dd6bf38f97236a9549a4b80c6c9e5b.svg'
import imgArrowUp from '@/assets/figma/5e22dcd217a5a0d68a17d4fabf05228f570365fd.svg'
import imgLogo from '@/assets/logo-goldnara.svg'
import imgGlobe from '@/assets/figma/c1e014a3b0ac8880c90a4491cfa8f992b1925f96.svg'
import imgChevronDown from '@/assets/figma/0b0edb376dfb33146485f791e342ea5b4ed60db5.svg'
import imgTooltipBubble from '@/assets/figma/6281b9c46e19755c9f42755764c215fbd57e1a8a.svg'

const props = defineProps({
  // 현재 화면에 해당하는 메뉴 key. 해당 항목만 강조된다.
  active: { type: String, default: '' },
  // 기준가 줄(오늘의 거래 기준가 + 펼침 시세표)을 보일지.
  // REAL GOLD TRADE 안쪽 화면들은 끈다(2026-10-07 지시) — '오늘의 시세' 화면은
  // 본문이 바로 그 내용이라 겹치고, '소개' 화면은 어두운 히어로 위에 파란 띠가 얹힌다.
  // 끄면 헤더가 35px 짧아진다. 쓰는 쪽에서 HEADER_H - RATES_BAR_H 로 자리를 잡아야 한다.
  showRates: { type: Boolean, default: true },
  // 로그인·로그아웃 버튼 노출. 회원 기능이 있는 자사몰에서만 켠다.
  showLogin: { type: Boolean, default: false },
  // 로그인 상태. 자사몰처럼 인증 스토어가 있는 프로젝트에서 넘겨준다.
  loggedIn: { type: Boolean, default: false },
  // "로그인하고 더 많은 혜택을 받아보세요." 말풍선 사용 여부.
  // 켜져 있어도 항상 보이지는 않는다 — 로그인 버튼에 마우스를 올렸을 때만 뜬다.
  showLoginTip: { type: Boolean, default: true },
  // 펼침 시세표 행. 넘기지 않으면 아래 기본값(옛 자사몰 헤더 값)을 쓴다.
  ratesTable: { type: Array, default: () => [] },
  // 시세 4종. 넘기지 않으면 아래 기본값(퍼블리싱 값)을 그대로 쓴다.
  // 자사몰처럼 시세 API 가 있는 프로젝트에서 실제 값을 넘겨준다.
  rates: { type: Array, default: null },
})

// 로그인 버튼 호버 상태. 말풍선은 이때만 표시한다.
const loginHover = ref(false)

// 시세 기본값(퍼블리싱 값). **시세를 받기 전과 못 받았을 때**만 보인다.
// 평소에는 useHeaderRates 가 받아 온 값이 이 자리를 덮는다.
const DEFAULT_RATES = [
  { label: 'GOLD', price: '951,000원', change: '14,000', direction: 'down' },
  { label: 'Silver', price: '14,490원', change: '690', direction: 'up' },
  {
    label: '중매거래',
    price: '951,000원',
    change: '14,000',
    direction: 'down',
  },
  {
    label: '즉시거래',
    price: '951,000원',
    change: '14,000',
    direction: 'down',
  },
]

// 살아 있는 시세. 자사몰 서버가 공용 서버에서 받아 꾸며 내려 준다.
// 못 받으면 값이 null 로 남고 아래 computed 가 퍼블리싱 값을 그대로 쓴다 —
// 상단이 빈 채로 뜨는 것보다 낫고, 헤더가 가장 먼저 그려지는 자리라 눈에 띈다.
const { rates: liveRates, ratesTable: liveTable, asOf: liveAsOf } = useHeaderRates()

// 우선순위는 셋이다 — 넘겨받은 값 > 살아 있는 값 > 퍼블리싱 값.
// 쓰는 쪽에서 :rates 를 넘기면 그것이 이긴다(지금은 넘기는 화면이 없다).
const tickerItems = computed(() => {
  if (props.rates?.length) return props.rates
  if (liveRates.value?.length) return liveRates.value
  return DEFAULT_RATES
})

// 메뉴 표는 푸터와 같이 쓴다 — data/siteMenu.js
const menu = SITE_MENU

const emit = defineEmits(['login', 'logout', 'rates-toggle', 'language-toggle'])

// ── 다국어 ──────────────────────────────────────────────────────────
// 지구본과 그 옆 펼침 표시를 가린다(2026-09-26 요청).
// 다국어 판이 나오면 이 값만 true 로 되돌리면 된다.
const SHOW_LANGUAGE = false

// ── 시세표 ──────────────────────────────────────────────────────────
// 메뉴 바 아래 30px 띠를 누르면 펼쳐지는 표.
//
// 새로 만든 것이 아니라 신규 퍼블리싱 이전 자사몰 헤더
// (goldnaramall-client/src/components/AppHeader.vue)에 있던 것을 그대로 옮겼다.
// 값도 그때 박아 둔 그대로다 — 시세 띠와 마찬가지로 아직 연결된 API 가 없다.
// /api/v1/market-prices 는 금속 하나씩만 주고 살 때/팔 때 구분이 없다.
//
// 레이어로 띄우지 않고 흐름 안에 둔다(요청). 헤더 상자가 통째로 커지므로
// 흐름에 놓인 화면에서는 본문이 그만큼 아래로 밀린다.
const RATES_AS_OF = '2026.06.06'

const DEFAULT_RATES_TABLE = [
  { name: '순금시세', label: 'Gold 24k 3.75g', buyPrice: '922,500원', buyChange: '-13,600원', sellPrice: '902,700원', sellChange: '-13,300원' },
  { name: '18K 금시세', label: 'Gold 18k 3.75g', buyPrice: '', buyChange: '', sellPrice: '663,500원', sellChange: '-9,800원' },
  { name: '14K 금시세', label: 'Gold 14k 3.75g', buyPrice: '', buyChange: '', sellPrice: '514,500원', sellChange: '-7,600원' },
  { name: '백금시세', label: 'Platinum 3.75g', buyPrice: '376,000원', buyChange: '-17,000원', sellPrice: '368,000원', sellChange: '-17,000원' },
  { name: '은시세', label: 'Silver 3.75g', buyPrice: '14,100원', buyChange: '-600원', sellPrice: '13,800원', sellChange: '-600원' },
]

const rows = computed(() => {
  if (props.ratesTable?.length) return props.ratesTable
  if (liveTable.value?.length) return liveTable.value
  return DEFAULT_RATES_TABLE
})

// 표 머리의 "2026.06.06 기준". 살아 있는 값이 오면 그 시세의 날짜다.
const asOfText = computed(() => liveAsOf.value || RATES_AS_OF)

const ratesOpen = ref(false)
const toggleRates = () => {
  ratesOpen.value = !ratesOpen.value
  // 헤더를 흐름 밖에 두는 화면(홈 · IR)은 이 값을 받아 제 본문을 밀어야 한다.
  emit('rates-toggle', ratesOpen.value)
}

// ── 메뉴 펼침 ───────────────────────────────────────────────────────
// children 이 있는 항목(REAL GOLD TRADE)은 누르면 작은 메뉴가 펼쳐진다.
// Figma 8236:23710 — 시안 이름이 "real gold trade 클릭" 이라 호버가 아니라 누름이다.
//
// 자리는 시안 그대로 176x90, 모서리 10, 그림자 0 3px 15px rgba(0,0,0,.2),
// 글은 14px 600 / 줄높이 20, 왼쪽 15, 위 20 과 50.
//
// 세로 자리만 시안 값을 그대로 쓸 수 없다. 시안 헤더는 50+90+40=180 이고
// 우리 헤더는 40+60+35=135 다(CLAUDE.md, 의도한 차이). 시안에서 판은 메뉴 글자
// 아랫변에서 10px 아래에 걸려 있다(글자 154~178, 판 188). 그 관계를 그대로 옮겨
// 항목 상자 기준 top: calc(100% + 10px) 으로 건다.
const openKey = ref('')

const toggleMenu = (key) => {
  openKey.value = openKey.value === key ? '' : key
}

// 바깥을 누르거나 Esc 를 누르면 닫는다. 메뉴를 펼친 동안만 듣는다.
const closeMenu = () => {
  openKey.value = ''
}

const onDocPointer = (e) => {
  if (!menuEl.value?.contains(e.target)) closeMenu()
}

const onDocKey = (e) => {
  if (e.key === 'Escape') closeMenu()
}

const menuEl = ref(null)

watch(openKey, (key) => {
  if (key) {
    document.addEventListener('pointerdown', onDocPointer)
    document.addEventListener('keydown', onDocKey)
  } else {
    document.removeEventListener('pointerdown', onDocPointer)
    document.removeEventListener('keydown', onDocKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointer)
  document.removeEventListener('keydown', onDocKey)
})
</script>

<template>
  <!-- 스크롤해도 붙어 있는다(2026-09-27 지시). 연출은 걸지 않는다 -
       홈은 인트로에 맞춰 헤더가 내려오지만 여기는 처음부터 그 자리다.
       fixed 가 아니라 sticky 인 이유: 흐름에 남아 있어 본문을 밀지 않는다.
       relative 를 대신해도 안쪽 절대배치의 기준 상자는 그대로다. -->
  <header class="sticky top-0 z-50 w-full bg-white">
    <!-- 시세 바 377:21382 -->
    <div class="relative h-[40px] w-full overflow-hidden bg-black">
      <div
        class="relative mx-auto flex h-full max-w-[1280px] items-center justify-between px-[20px]"
      >
        <!-- 377:21383 장식 이미지 (Figma left 317 = 컨테이너 기준 102) -->
        <div
          aria-hidden
          class="pointer-events-none absolute left-[102px] top-[-70px] flex h-[177.737px] w-[257.132px] items-center justify-center"
        >
          <div class="flex-none rotate-[-11.89deg]">
            <div class="relative h-[132.153px] w-[234.939px]">
              <img
                alt=""
                class="absolute inset-0 size-full max-w-none object-cover"
                :src="imgTickerGlow"
              />
            </div>
          </div>
        </div>

        <!-- 377:21417 오늘의 시세.
             글자는 "오늘의 거래 기준가"였고 옆에 펼침 화살표가 있었다. 2026-09-26 에
             글자를 줄이고 화살표를 뗐다 — 펼치는 일은 아래 시세표 띠가 맡는다. -->
        <span
          class="relative shrink-0 whitespace-nowrap text-[16px] font-bold leading-[22px] text-white"
          >오늘의 시세</span
        >

        <!-- 377:21384 시세.
             퍼블리싱은 오른쪽 끝에 붙여 두지만 오른쪽에서 왼쪽으로 흘려보낸다.
             칸이 늘거나 화면이 좁아도 뒤쪽이 잘려 안 보이는 일이 없다. -->
        <RatesTicker
          class="relative ml-[40px] h-full flex-1"
          :items="tickerItems"
          :gap="60"
        >
          <template #default="{ item }">
            <p
              class="whitespace-nowrap text-[15px] font-medium leading-[21px] text-white"
            >
              {{ item.label }}
            </p>
            <div class="ml-[10px] flex shrink-0 items-center gap-[5px]">
              <p
                class="whitespace-nowrap text-[15px] font-semibold leading-[21px] text-white"
              >
                {{ item.price }}
              </p>
              <div v-if="item.change" class="flex shrink-0 items-center gap-[2px]">
                <img
                  alt=""
                  class="size-[12px] shrink-0"
                  :src="item.direction === 'up' ? imgArrowUp : imgArrowDown"
                />
                <p
                  class="whitespace-nowrap text-[13px] font-semibold leading-[18px]"
                  :class="
                    item.direction === 'up'
                      ? 'text-[#8cbaff]'
                      : 'text-[#ff4372]'
                  "
                >
                  {{ item.change }}
                </p>
              </div>
            </div>
          </template>
        </RatesTicker>
      </div>
    </div>

    <!-- 헤더 377:21421 -->
    <!-- 아래 선은 메뉴와 시세표 띠를 가른다. 둘 다 흰 바탕이라 선이 없으면
         띠가 메뉴 줄의 일부처럼 붙어 보이고 활성 메뉴의 주황 막대가 허공에 뜬다.
         box-sizing 이 border-box 라 60 안에서 1px 을 먹는다 - 높이는 안 바뀐다. -->
    <!-- z-10 은 펼침 메뉴 때문이다. REAL GOLD TRADE 의 작은 메뉴가 이 줄 안에서
         아래로 떨어지는데, 아래 기준가 줄이 DOM 에서 나중이라 그냥 두면 그 위를 덮는다
         (소개 칸이 네이비 띠에 가려졌다). 줄 자체를 위로 올려 둔다. -->
    <div
      class="relative z-10 h-[60px] w-full border-b border-solid border-[#e2e2e2] bg-white"
    >
      <div
        class="relative mx-auto flex h-full max-w-[1280px] items-center justify-between px-[20px]"
      >
        <!-- 377:21441 로고.
             Figma 는 마크와 글자를 따로 두고 좌표로 맞춰 놨지만 실제 로고는 한 파일이다.
             칸(184x40)과 로고 비율(4.65:1)이 거의 같아 object-contain 으로 채운다. -->
        <a
          href="/"
          class="block h-[40px] w-[184px] shrink-0"
          aria-label="골드나라"
        >
          <img
            alt="골드나라"
            class="h-full w-full object-contain object-left"
            :src="imgLogo"
          />
        </a>

        <!-- 377:21434 메뉴 (Figma 기준 화면 중앙 정렬) -->
        <!--
          Figma 는 1920 기준으로 메뉴를 화면 정중앙에 두고 항목 간격이 80px 이다.
          그 간격을 고정하면 1920 보다 좁은 실제 화면(서브페이지)에서 로고와 겹친다.
          그래서 간격만 화면 폭에 따라 줄인다 — 1920 에서는 정확히 80px 이 된다.

          ★ 상한은 60px 이다(자사몰은 2026-11-02, 여기는 11-05 에 칸이 1240 이 되면서 같이).
            칸이 1240 에서 더 넓어지지 않는데 간격만 80 까지 커지면 1920 에서 메뉴(913)가
            로고와 21px 겹친다. 1240 칸에서 메뉴가 들어갈 수 있는 상한은 약 71px 이다.

          굵기는 600 이다. 바로 아래 '오늘의 거래 기준가' 줄과 같은 값이다(2026-09-29 요청).
          한때 500 이었는데, 이 줄이 배율 안에 있어 노트북 폭(1366)에서 11.4px 로 그려진다.
          그 크기에서는 500 과 600 차이가 크기 차이처럼 보여, 활성 항목(600) 옆에
          선 항목들이 작아 보인다는 지적을 받았다. 활성 표시는 금색만으로 남는다.
        -->
        <nav
          ref="menuEl"
          class="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center whitespace-nowrap topbar-text font-bold leading-[24px] lg:flex"
          style="gap: clamp(20px, 4.16vw, 60px)"
        >
          <!-- 한 칸씩 상자로 감싼다. 펼침 메뉴가 그 상자를 기준으로 떨어지기 때문이다.
               상자는 글자 높이(24)만 차지하므로 줄 모양은 전과 같다. -->
          <div v-for="item in menu" :key="item.key" class="relative shrink-0">
            <!-- children 이 있으면 누름 메뉴라 <a> 가 아니라 <button> 이다.
                 <a> 로 두면 누르는 순간 주소가 바뀌어 펼칠 틈이 없다. -->
            <component
              :is="item.children ? 'button' : 'a'"
              :href="item.children ? null : item.href"
              :type="item.children ? 'button' : null"
              :aria-expanded="item.children ? openKey === item.key : null"
              :aria-haspopup="item.children ? 'true' : null"
              class="block transition-colors"
              :class="
                active === item.key
                  ? 'text-[#c4890b]'
                  : 'text-black hover:text-[#c4890b]'
              "
              @click="item.children ? toggleMenu(item.key) : null"
            >
              {{ item.label }}
              <!-- 활성 표시 막대는 없앴다(2026-09-28 지시). 금색 글자만으로 표시한다. -->
            </component>

            <!-- 펼침 메뉴. Figma 8241:3864 — 176x90, 모서리 10.
                 글자 상자 아랫변에서 10px 아래에 건다(위 주석 참고).
                 버튼 안이 아니라 밖이다 — <button> 안의 <a> 는 안 되는 마크업이다. -->
            <div
              v-if="item.children && openKey === item.key"
              class="absolute left-0 top-[calc(100%+10px)] z-30 h-[90px] w-[176px] rounded-[10px] bg-white text-left shadow-[0px_3px_15px_0px_rgba(0,0,0,0.2)]"
              data-node-id="8241:3864"
            >
              <a
                v-for="(sub, si) in item.children"
                :key="sub.key"
                :href="sub.href"
                class="absolute left-[15px] block whitespace-nowrap text-[14px] font-semibold leading-[20px] text-black transition-colors hover:text-[#c4890b]"
                :style="{ top: `${20 + si * 30}px` }"
              >
                {{ sub.label }}
              </a>
            </div>
          </div>
        </nav>

        <!-- 377:21423 언어 + 로그인 -->
        <!--        <div class="relative flex shrink-0 items-center gap-[20px]">-->
        <!--          <button type="button" class="flex shrink-0 items-center gap-[4px]" @click="emit('language-toggle')">-->
        <!--            <img alt="" class="size-[24px] shrink-0" :src="imgGlobe" />-->
        <!--            <img alt="" class="size-[18px] shrink-0" :src="imgChevronDown" />-->
        <!--          </button>-->
        <!--          &lt;!&ndash; 로그인 상태에서는 로그아웃이 있어야 한다. Figma 에는 없어 글자 버튼으로 옆에 둔다. &ndash;&gt;-->
        <!--          <button-->
        <!--            v-if="loggedIn"-->
        <!--            type="button"-->
        <!--            class="shrink-0 whitespace-nowrap text-[15px] font-medium leading-[22px] text-[#626161] transition-colors hover:text-black"-->
        <!--            @click="emit('logout')"-->
        <!--          >-->
        <!--            로그아웃-->
        <!--          </button>-->
        <!--          <button-->
        <!--            type="button"-->
        <!--            class="flex shrink-0 items-center justify-center rounded-[50px] bg-[#00264d] px-[20px] py-[7px]"-->
        <!--            @mouseenter="loginHover = true"-->
        <!--            @mouseleave="loginHover = false"-->
        <!--            @focus="loginHover = true"-->
        <!--            @blur="loginHover = false"-->
        <!--            @click="emit('login')"-->
        <!--          >-->
        <!--            <span class="whitespace-nowrap topbar-text font-medium leading-[24px] text-white">-->
        <!--              {{ loggedIn ? '마이페이지' : '로그인' }}-->
        <!--            </span>-->
        <!--          </button>-->
        <!--        </div>-->

        <!-- 380:22861 로그인 유도 말풍선 (헤더 아래로 내려온다) -->
        <div
          v-if="showLoginTip && !loggedIn"
          class="pointer-events-none absolute right-[20px] top-[66px] z-10 h-[38px] w-[220px] transition-opacity duration-200"
          :class="loginHover ? 'opacity-100' : 'opacity-0'"
        >
          <img alt="" class="absolute inset-0 size-full max-w-none" :src="imgTooltipBubble" />
          <p class="absolute left-[13px] top-[14px] whitespace-nowrap text-[13px] font-medium leading-[18px] text-white">
            로그인하고 더 많은 혜택을 받아보세요.
          </p>
        </div>
      </div>
    </div>

    <!-- 시세표 띠 (2026-09-26 요청).
         메뉴 바 90 을 60 + 30 으로 나눈 아래쪽 30 이다. 글자 앞뒤의 표시는
         닫혀 있을 때 아래를 가리켜 "누르면 아래가 펼쳐진다"를 알린다. -->
    <!-- 바탕은 골드에비뉴의 파란 띠 색이다(#0074E8 = rgb(0,116,232), 2026-10-06 지시).
         goldavenue.com 상단에서 직접 읽은 값이다.

         2026-09-28 에는 네이비(#00264D)였다. 그때는 "골드에비뉴 참고"를 **자리**로
         읽었다 - 그 사이트 상단은 세 겹이고 이 줄에 해당하는 시세 바가 네이비,
         파란 줄은 맨 위 광고 띠였기 때문이다. 2026-10-06 에 클라이언트가 그 화면을
         직접 보내며 "이 파란색" 이라고 못박아 **색**으로 다시 정했다.
         지난 비교(_review/topbar-color-20260928)의 결론보다 이쪽이 나중이다.
         되돌리지 말 것.

         자사몰에서 바로 아래 오는 카테고리 바는 그쪽 메뉴와 같은 회색(#F2F4F6)으로
         둔다 - 두 줄 다 진하게 하면 94px 가 통째로 눌리고 금색 호버를 내줘야 한다.

         아래 선(#e2e2e2)은 뺀다(2026-09-29 지시). 홈 메인에서는 이 띠 밑이 곧바로
         어두운 히어로라 회색 선이 흰 줄로 도드라졌다. 대신 버튼을 34 -> 35 로 올려
         이 줄의 35px 와 헤더 합계 135 를 그대로 지킨다 - 높이가 바뀌면
         useFitHeight 의 HEADER_H 와 홈의 슬롯 계산이 같이 어긋난다. -->
    <div v-if="showRates" class="relative w-full bg-[#0074e8]">
      <button
        type="button"
        class="mx-auto flex h-[35px] w-full max-w-[1280px] items-center justify-center gap-[8px] px-[20px] topbar-text font-medium leading-[24px] text-white transition-colors hover:text-white/80"
        :aria-expanded="ratesOpen"
        @click="toggleRates"
      >
        <span
          aria-hidden="true"
          class="topbar-caret leading-none transition-transform"
          :class="ratesOpen ? '' : 'rotate-180'"
          >▲</span
        >
        <span>오늘의 거래 기준가(클릭)</span>
        <span
          aria-hidden="true"
          class="topbar-caret leading-none transition-transform"
          :class="ratesOpen ? '' : 'rotate-180'"
          >▲</span
        >
      </button>

      <!-- 펼쳐지는 표. 레이어가 아니라 흐름 안이다 — 그림자를 두지 않고
           띠와 같은 배경을 써서 "띠가 늘어난 것"으로 보이게 한다. -->
      <div v-if="ratesOpen" class="w-full border-y border-solid border-[#e2e2e2] bg-white">
        <div class="mx-auto w-full max-w-[1280px] px-[20px] py-[12px]">
          <div class="w-full overflow-hidden rounded-[10px] border border-solid border-[#dfe4ec]">
            <div
              class="grid grid-cols-[1.1fr_1fr_1fr] items-center border-b border-solid border-[#e5e7eb] bg-[#f8f9fb] px-[18px] py-[8px]"
            >
              <!-- 이름과 라벨을 한 줄에 둔다(2026-10-01: "표를 한 줄로 더 줄이자").
                   두 줄일 때 줄 하나가 68(캔버스)이었는데 그중 51 이 이 두 줄이었다.
                   여백은 이미 짤 만큼 짠 뒤라 더 줄이려면 여기밖에 없었다.
                   items-baseline 이라 크기가 다른 두 글자의 밑선이 맞는다. -->
              <div class="flex flex-wrap items-baseline gap-x-[8px]">
                <p class="rt-name font-bold text-[#202632]">오늘의 금 · 은 · 백금 시세</p>
                <p class="rt-sub text-[#8a8f99]">{{ asOfText }} 기준</p>
              </div>
              <p class="text-center rt-sub font-semibold text-[#48566a]">
                내가 살 때 <span class="font-normal text-[#8a8f99]">(위탁 중매 기준)</span>
              </p>
              <p class="text-center rt-sub font-semibold text-[#48566a]">
                내가 팔 때 <span class="font-normal text-[#8a8f99]">(위탁 중매 기준)</span>
              </p>
            </div>

            <div
              v-for="row in rows"
              :key="row.name"
              class="grid grid-cols-[1.1fr_1fr_1fr] items-center border-b border-solid border-[#edf0f4] px-[18px] py-[8px] last:border-b-0"
            >
              <div class="flex flex-wrap items-baseline gap-x-[8px]">
                <p class="rt-name font-bold text-[#202632]">{{ row.name }}</p>
                <p class="rt-sub text-[#8a8f99]">{{ row.label }}</p>
              </div>
              <!-- 칸을 반으로 갈라 가격은 오른쪽 끝, 변동은 왼쪽 끝에 붙인다.
                   둘이 칸 가운데를 사이에 두고 맞닿으므로 어느 줄이든 가격의 끝자리가
                   같은 세로선에 선다. 한 덩어리로 가운데 정렬하면 변동 글자 길이
                   ("+12,054원" 과 "+45원")에 따라 가격이 좌우로 밀린다(2026-10-19 제보). -->
              <div class="grid grid-cols-2 items-baseline gap-[8px]">
                <!-- 살 때를 안 매기는 등급(18K·14K)은 '제품시세적용' 이 금액 자리에
                     그대로 선다. 칸을 통째로 쓰고 가운데 세우면 위아래 금액과 끝선이
                     어긋난다 - 글자든 숫자든 같은 세로선이어야 한다. -->
                <p class="rt-price text-right font-bold text-[#111827]">
                  {{ row.buyPrice || '제품시세적용' }}
                </p>
                <p class="rt-sub text-left text-[#2b8ee8]">{{ row.buyChange }}</p>
              </div>
              <div class="grid grid-cols-2 items-baseline gap-[8px]">
                <p class="rt-price text-right font-bold text-[#111827]">{{ row.sellPrice }}</p>
                <p class="rt-sub text-left text-[#2b8ee8]">{{ row.sellChange }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
/* ── 기준가 표의 글자 크기 ────────────────────────────────────────────
   페이지 배율(zoom)을 글자에서만 되돌린다. 표는 1920 캔버스 안에 있어서
   그냥 두면 창이 좁아질 때 글자까지 줄어든다 - 창 1188 이하(배율 하한
   0.619)에서 12px 지정이 7.4px 로 그려졌다. 한글 획이 뭉개지는 크기다.

   지정값을 키우는 방법은 못 쓴다. 하한에서 12 를 지키려면 20px 이 필요한데
   그러면 1920 에서 표가 과하게 커지고, 창 폭마다 크기가 또 달라진다.

   calc(Npx / var(--z)) 로 두면 어느 창에서든 N px 로 그려진다. 여백과
   칸 폭은 캔버스 단위 그대로 줄어든다 - 표는 데이터 패널이라 글자만
   고정하는 편이 읽기 좋다.

   가장 작은 것이 13 이다. 12 로 두면 기준선에 딱 걸쳐서 여유가 없다.
   --z 가 없는 화면에서는 fallback 1 이라 아무 일도 안 한다.

   행간은 2026-09-30 에 줄였다("표가 너무 높다"). 1.5/1.45/1.4 -> 1.35/1.3/1.3.
   글자 크기는 안 건드린다 - 여기는 12px 기준선이 걸려 있어 더 못 줄인다.
   행간 0.15 는 배율을 되돌린 글자라 한 줄에 배율분만큼 더 줄어든다
   (13px x 0.15 / 0.728 = 캔버스 2.7). 여섯 줄이면 캔버스 33 이다. */
.rt-sub {
  font-size: calc(13px / var(--z, 1));
  line-height: 1.35;
}
.rt-name {
  font-size: calc(14px / var(--z, 1));
  line-height: 1.3;
}
.rt-price {
  font-size: calc(16px / var(--z, 1));
  line-height: 1.3;
}

/* ── 상단 띠 글자 (2026-10-13 결정) ────────────────────────────────────
   이 줄들은 페이지 배율(--z) 안이라 창이 좁아지면 글자도 같이 줄어든다.
   1366 노트북에서 11.38px 까지 떨어져 한글 획이 뭉개졌다.
   2026-10-06 에 12px 하한, 2026-10-07 에 자사몰 카테고리만 14px 로 받쳤다가
   2026-10-13 에 **배율 밖으로 뺐다** — 언제나 16px 이다.

     지정 = 16px / 배율   ->   그려지는 크기 = 16px (창 폭과 무관)

   16 은 골드에비뉴(goldavenue.com)의 헤더 메뉴 크기다. 그쪽은 페이지 배율이
   없어 1440 에서도 16px 그대로라, "골드에비뉴와 같게" 는 곧 "안 줄어들게" 다.
   (글꼴은 그쪽이 Inter, 우리는 한글 때문에 Pretendard 다.)

       1920 이상   16   (그대로)
       1440        16   (전에는 12)
       1366        16   (전에는 12)
       1188 이하   16   (전에는 12 / 자사몰 카테고리 14)

   ★ 같은 줄에 선 것끼리 **묶어서** 건다. 하나만 받치면 옆이 작아져 위계가
     뒤집힌다. 2026-10-07 에 자사몰 카테고리만 14 로 어긋나 있던 것이
     이것으로 다시 네 곳 모두 16 이 되었다.

   높이는 안 건드려도 된다. 행간이 24 로 못박혀 있어 글자가 커져도 줄상자는
   24 그대로다 - 메뉴·로그인·카테고리는 전부터 그랬고, 기준가 줄도
   2026-10-13 에 같이 박았다(그전엔 normal 이라 1188 에서 38.8 까지 컸다).

   검정 시세 바는 일부러 뺐다(2026-10-06 요청). 숫자가 흐르는 별개 띠다. */
.topbar-text {
  font-size: calc(16px / var(--z, 1));
}
/* 기준가 줄의 꺾쇠(▲). 글자를 배율 밖으로 뺐으니 이것도 같이 뺀다 -
   10px 고정이면 1188 에서 6.2px 까지 떨어져 16px 글자 옆에서 어긋난다. */
.topbar-caret {
  font-size: calc(10px / var(--z, 1));
}
</style>
